/**
 * Privacy Policy — content from inside_access_privacy_policy_markdown.md
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <article aria-labelledby="privacy-title">
        <h1 id="privacy-title" className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Privacy Policy
        </h1>
        <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
          <strong>Last updated:</strong> 4 February 2026
        </p>

        <section className="mb-8" aria-labelledby="privacy-summary">
          <h2 id="privacy-summary" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Privacy Summary (Plain English)
          </h2>
          <p className="mb-2 font-semibold" style={{ color: 'var(--text-primary)' }}>In short:</p>
          <ul className="list-disc pl-6 space-y-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>We only collect information that is needed to run and improve InsideAccess.</li>
            <li>We do <strong>not</strong> sell your personal data.</li>
            <li>We use <strong>internal, first‑party metrics</strong> to understand how the platform is used and to improve accessibility, reliability, and moderation.</li>
            <li>We do <strong>not</strong> use advertising or cross‑site tracking cookies.</li>
            <li>You are in control of your data and can request access or deletion at any time.</li>
          </ul>
        </section>

        <section className="mb-8" aria-labelledby="who-we-are">
          <h2 id="who-we-are" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            1. Who We Are
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            InsideAccess (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is an online platform designed to support discussion, discovery, and promotion of accessible media content, including information about audio description, subtitles, and signed content.
          </p>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            For the purposes of UK data protection law, InsideAccess is the <strong>data controller</strong>.
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            <strong>Contact:</strong>{' '}
            <a href="mailto:support@insideaccess.org" className="underline" style={{ color: 'var(--color-focus)' }}>
              support@insideaccess.org
            </a>
          </p>
        </section>

        <section className="mb-8" aria-labelledby="what-we-collect">
          <h2 id="what-we-collect" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            2. What Information We Collect
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            We only collect information that is necessary to operate and improve the platform.
          </p>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            2.1 Information You Provide
          </h3>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>This may include:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Your name or display name</li>
            <li>Email address</li>
            <li>Account login details</li>
            <li>Content you post (comments, discussions, feedback)</li>
            <li>Information you provide when contacting us</li>
          </ul>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            2.2 Information Collected Automatically
          </h3>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
            When you use InsideAccess, we may collect:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-2" style={{ color: 'var(--text-secondary)' }}>
            <li>IP address</li>
            <li>Browser and device information</li>
            <li>Pages visited and interactions with the platform</li>
            <li>Date and time of access</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            This information is collected using first‑party logging and monitoring tools.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="internal-metrics">
          <h2 id="internal-metrics" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            3. Internal Metrics and Platform Analytics
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            We use <strong>internal, first‑party metrics</strong> to understand how InsideAccess is used and to operate the service effectively. These metrics may include:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Account creation and login activity</li>
            <li>Feature usage (e.g. posts, comments, accessibility feedback)</li>
            <li>Aggregate usage trends</li>
            <li>System performance, error logs, and moderation activity</li>
          </ul>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>These metrics:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Are used <strong>only within InsideAccess</strong></li>
            <li>Are <strong>not shared or sold</strong> to third parties for advertising</li>
            <li>Do <strong>not</strong> track users across other websites or services</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            We process this data as part of our legitimate interests in operating, securing, and improving the platform.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="how-we-use">
          <h2 id="how-we-use" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            4. How We Use Your Information
          </h2>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>We use your information to:</p>
          <ul className="list-disc pl-6 space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li>Provide and operate the InsideAccess platform</li>
            <li>Create and manage user accounts</li>
            <li>Enable participation in community discussions</li>
            <li>Improve accessibility, usability, and content relevance</li>
            <li>Monitor and prevent abuse, fraud, or misuse</li>
            <li>Respond to enquiries and support requests</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8" aria-labelledby="legal-basis">
          <h2 id="legal-basis" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            5. Legal Basis for Processing
          </h2>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
            Under UK GDPR, we process personal data based on:
          </p>
          <ul className="list-disc pl-6 space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li><strong>Consent</strong> – where you have given clear permission</li>
            <li><strong>Contract</strong> – where processing is necessary to provide the service</li>
            <li><strong>Legitimate interests</strong> – to operate, secure, and improve the platform</li>
            <li><strong>Legal obligation</strong> – where required by law</li>
          </ul>
        </section>

        <section className="mb-8" aria-labelledby="cookies">
          <h2 id="cookies" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            6. Cookies and Similar Technologies
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            InsideAccess uses cookies and similar technologies <strong>only where necessary</strong> to:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Maintain secure login sessions</li>
            <li>Protect the platform from misuse</li>
            <li>Remember basic user preferences</li>
          </ul>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            We do <strong>not</strong> use advertising or cross‑site tracking cookies.
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            If we introduce non‑essential cookies in the future, we will update this policy and provide appropriate user controls.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="storage-security">
          <h2 id="storage-security" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            7. Data Storage and Security
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            Your data is stored using secure, reputable third‑party infrastructure providers.
          </p>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
            We take appropriate technical and organisational measures to protect personal data against:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Unauthorised access</li>
            <li>Loss or destruction</li>
            <li>Accidental disclosure</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            Access to personal data is limited to those who need it to operate and support the platform.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="third-party">
          <h2 id="third-party" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            8. Third‑Party Services
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            We may use trusted third‑party services to help operate InsideAccess, such as hosting, authentication, and infrastructure monitoring providers.
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            These providers process personal data only on our instructions and are required to comply with applicable data protection laws.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="retention">
          <h2 id="retention" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            9. Data Retention
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            We retain personal data only for as long as necessary to:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Provide the service</li>
            <li>Meet legal, accounting, or regulatory requirements</li>
            <li>Resolve disputes or enforce our terms</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            You may request deletion of your account and associated data at any time.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="your-rights">
          <h2 id="your-rights" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            10. Your Rights
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            Under UK GDPR, you have the right to:
          </p>
          <ul className="list-disc pl-6 space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <li>Access your personal data</li>
            <li>Correct inaccurate or incomplete data</li>
            <li>Request deletion of your data</li>
            <li>Restrict or object to processing</li>
            <li>Request data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            To exercise these rights, contact{' '}
            <a href="mailto:support@insideaccess.org" className="underline" style={{ color: 'var(--color-focus)' }}>
              support@insideaccess.org
            </a>
            .
          </p>
        </section>

        <section className="mb-8" aria-labelledby="children">
          <h2 id="children" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            11. Children&apos;s Privacy
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            InsideAccess is not intended for children under the age of 13. We do not knowingly collect personal data from children.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="changes">
          <h2 id="changes" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            12. Changes to This Policy
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            We may update this Privacy Policy from time to time. Any changes will be posted on this page and the &quot;Last updated&quot; date will be revised.
          </p>
        </section>

        <section className="mb-8" aria-labelledby="complaints">
          <h2 id="complaints" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            13. Complaints
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            If you have concerns about how we handle your personal data, please contact us first.
          </p>
          <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
            You also have the right to lodge a complaint with the UK Information Commissioner&apos;s Office (ICO):
          </p>
          <a
            href="https://www.ico.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="underline break-all"
            style={{ color: 'var(--color-focus)' }}
          >
            https://www.ico.org.uk
          </a>
        </section>
      </article>
    </div>
  )
}
